import { createFileRoute } from "@tanstack/react-router";

// Verbatim text from the supplied Data Processing Agreement.
const documentText = {
  "title": "DATA PROCESSING AGREEMENT",
  "subtitle": "SaaSed.co Limited (Processor) and the Customer (Controller) — effective 03.11.2026",
  "intro": [
    "This Data Processing Agreement (the \"DPA\") is between the customer that accepts it (the \"Controller\") and SaaSed.co Limited, Reg. no. HE482282, 2410 Egkomi, Nicosia, Cyprus (the \"Processor\"). The Controller accepts this DPA by accepting the Dioro Terms of Use, which incorporate it, or by signing it.",
    "This DPA applies to all processing of personal data carried out by the Processor on the Controller's behalf, including under the Dioro Terms of Use and any Dioro Order Form, and under any consultancy agreement between the Parties. Where any of those documents conflicts with this DPA on the processing of personal data, this DPA prevails."
  ],
  "sections": [
    {
      "title": "1. Definitions",
      "clauses": [
        {
          "number": "1.1",
          "text": "\"Data Protection Law\" means Regulation (EU) 2016/679 and Cyprus data protection law, and, where applicable, the UK General Data Protection Regulation and the Data Protection Act 2018. \"Personal Data\", \"processing\", \"controller\", \"processor\", \"data subject\", \"supervisory authority\" and \"personal data breach\" have the meanings given in Data Protection Law."
        },
        {
          "number": "1.2",
          "text": "\"Processing Details\" means the description of the processing set out in Annex 1. \"Security Measures\" means the measures set out in Annex 2."
        }
      ]
    },
    {
      "title": "2. Roles and scope",
      "clauses": [
        {
          "number": "2.1",
          "text": "The Controller is the controller of the Personal Data. The Processor processes the Personal Data only as a processor acting on the Controller's documented instructions."
        },
        {
          "number": "2.2",
          "text": "The subject matter, duration, nature, purpose, categories of data subject and categories of Personal Data are set out in Annex 1."
        },
        {
          "number": "2.3",
          "text": "The Controller is responsible for the lawfulness of its instructions and for having a lawful basis for the processing, including for informing its own users that their usage data is analysed by a third party."
        }
      ]
    },
    {
      "title": "3. Processor obligations",
      "clauses": [
        {
          "number": "3.1",
          "text": "The Processor shall process the Personal Data only for the purposes in Annex 1 and only on the Controller's instructions, and shall not process it for its own purposes."
        },
        {
          "number": "3.2",
          "text": "The Processor shall inform the Controller if, in its opinion, an instruction infringes Data Protection Law, and may suspend that instruction until the point is resolved."
        },
        {
          "number": "3.3",
          "text": "The Processor shall implement and maintain the Security Measures, and shall not materially reduce them during the term."
        },
        {
          "number": "3.4",
          "text": "The Processor shall ensure that each person it authorises to process the Personal Data is subject to a duty of confidentiality and processes it only as necessary for the purposes in Annex 1. Access shall be limited to the named personnel identified to the Controller and shall be granted on a least-privilege basis."
        },
        {
          "number": "3.5",
          "text": "The Processor shall not create any statistical or derived output from the Personal Data other than the findings and analysis described in Annex 1 and aggregated, anonymised benchmark data from which neither the Controller nor any data subject can be identified, whether directly or by inference."
        }
      ]
    },
    {
      "title": "4. Sub-processors",
      "clauses": [
        {
          "number": "4.1",
          "text": "The Controller authorises the sub-processors listed in Annex 3. The Processor shall impose data protection obligations on each sub-processor no less protective than those in this DPA and remains liable for their performance."
        },
        {
          "number": "4.2",
          "text": "The Processor shall give the Controller at least fourteen (14) days' written notice before adding or replacing a sub-processor. The Controller may object on reasonable data protection grounds, in which case the Parties shall discuss in good faith; if no resolution is reached the Controller may terminate the affected services without penalty."
        }
      ]
    },
    {
      "title": "5. Assistance to the Controller",
      "clauses": [
        {
          "number": "5.1",
          "text": "The Processor shall, on request and at the Controller's cost where the request is not caused by the Processor's breach, assist the Controller with: responding to data subject requests; data protection impact assessments; and consultations with a supervisory authority."
        },
        {
          "number": "5.2",
          "text": "Where the Processor receives a request directly from a data subject relating to the Personal Data, it shall not respond substantively but shall forward the request to the Controller without undue delay."
        }
      ]
    },
    {
      "title": "6. Personal data breach",
      "clauses": [
        {
          "number": "6.1",
          "text": "The Processor shall notify the Controller without undue delay, and in any event within forty-eight (48) hours, of becoming aware of a personal data breach affecting the Personal Data."
        },
        {
          "number": "6.2",
          "text": "The notification shall include, so far as known: the nature of the breach, the categories and approximate number of data subjects and records affected, the likely consequences, and the measures taken or proposed. Where the information is not all available at once, the Processor shall provide it in phases without further undue delay."
        },
        {
          "number": "6.3",
          "text": "The Processor shall not notify any third party of a breach affecting the Personal Data without the Controller's prior written consent, unless required by law."
        }
      ]
    },
    {
      "title": "7. International transfers",
      "clauses": [
        {
          "number": "7.1",
          "text": "The Processor is established in the Republic of Cyprus and processes the Personal Data within the European Economic Area and the United Kingdom, which is covered by an EU adequacy decision."
        },
        {
          "number": "7.2",
          "text": "Where a transfer of the Personal Data outside the United Kingdom or the European Economic Area is required, the Processor shall not make that transfer without the Controller's prior written consent and without a valid transfer mechanism in place under Data Protection Law, including where applicable the standard contractual clauses and the UK International Data Transfer Addendum."
        }
      ]
    },
    {
      "title": "8. Deletion and return",
      "clauses": [
        {
          "number": "8.1",
          "text": "The Processor shall delete the Personal Data within thirty (30) days of the earlier of (a) completion of the purposes in Annex 1 and (b) termination of the relevant agreement, and shall confirm deletion in writing on request."
        },
        {
          "number": "8.2",
          "text": "Clause 8.1 does not apply to copies the Processor is required by law to retain, or to copies held in routine backups pending their expiry, which remain subject to this DPA for as long as they are held."
        },
        {
          "number": "8.3",
          "text": "The Processor shall, if the Controller requests before deletion, return the Personal Data in a commonly used electronic format."
        }
      ]
    },
    {
      "title": "9. Audit",
      "clauses": [
        {
          "number": "9.1",
          "text": "The Processor shall make available to the Controller, on reasonable written request and no more than once in any twelve-month period unless a breach has occurred, the information reasonably necessary to demonstrate compliance with this DPA, and shall respond to a reasonable security questionnaire."
        },
        {
          "number": "9.2",
          "text": "Any on-site audit shall be at the Controller's cost, on at least thirty (30) days' notice, during business hours, and shall not require the Processor to disclose another customer's confidential information."
        }
      ]
    },
    {
      "title": "10. General",
      "clauses": [
        {
          "number": "10.1",
          "text": "This DPA takes effect on the date the Controller accepts or signs it and continues for as long as the Processor processes the Personal Data."
        },
        {
          "number": "10.2",
          "text": "This DPA is governed by the laws of the Republic of Cyprus and the courts of the Republic of Cyprus have exclusive jurisdiction, consistent with the agreements to which the processing relates."
        },
        {
          "number": "10.3",
          "text": "The Processor may update this DPA. The version in force when the Controller accepts or signs it applies to the Controller until the Controller accepts or signs a later version. The Processor keeps every version available online."
        }
      ]
    }
  ],
  "annex1": [
    [
      "Item",
      "Detail"
    ],
    [
      "Subject matter",
      "Analysis of the Controller's Salesforce licence entitlement and usage, and advisory support on the Controller's Salesforce contracts."
    ],
    [
      "Duration",
      "From connection of Dioro until deletion under clause 8."
    ],
    [
      "Nature of processing",
      "Read-only extraction from the Controller's Salesforce orgs; storage; analysis; production of the Report; deletion."
    ],
    [
      "Purpose",
      "To identify licence waste, utilisation gaps and licence mix opportunities, to cross-check the Controller's internal figures, and to support the Controller in negotiating its Salesforce agreements."
    ],
    [
      "Categories of data subject",
      "The Controller's Salesforce users, being its employees, contractors and, where applicable, its partner or community users."
    ],
    [
      "Categories of Personal Data",
      "Name; username; business email address; user licence type; profile and permission set assignments; role; active status; login timestamps; source IP address where present in login history; API usage attributable to a user."
    ],
    [
      "Special category data",
      "None. The Processor shall not knowingly process special category data or criminal offence data under this DPA."
    ],
    [
      "Data excluded",
      "Customer, contact, lead, case and opportunity content; attachments and files; email and chat content; Salesforce contract pricing."
    ]
  ],
  "security": [
    {
      "number": "1.",
      "lead": "Access control.",
      "text": "Access to the Personal Data is restricted to named personnel on a least-privilege basis and removed within one business day of a person no longer needing it. Multi-factor authentication is enforced on all accounts with access."
    },
    {
      "number": "2.",
      "lead": "Encryption.",
      "text": "The Personal Data is encrypted in transit using TLS 1.2 or above and encrypted at rest."
    },
    {
      "number": "3.",
      "lead": "Segregation.",
      "text": "The Controller's data is logically separated from that of other customers."
    },
    {
      "number": "4.",
      "lead": "Minimisation.",
      "text": "Only the data categories in Annex 1 are extracted. Extraction is read-only; no write access is requested or exercised."
    },
    {
      "number": "5.",
      "lead": "Device and media.",
      "text": "The Personal Data is not stored on removable media and not stored on personal devices. Endpoints with access use full-disk encryption and automatic screen lock."
    },
    {
      "number": "6.",
      "lead": "Logging.",
      "text": "Access to the Personal Data is logged, and logs are retained for at least twelve months."
    },
    {
      "number": "7.",
      "lead": "Personnel.",
      "text": "Personnel with access are bound by written confidentiality obligations and receive data protection awareness training."
    },
    {
      "number": "8.",
      "lead": "Deletion.",
      "text": "Deletion under clause 8 is performed across primary storage and working copies, with backup copies expiring on the normal backup cycle."
    },
    {
      "number": "9.",
      "lead": "Incident response.",
      "text": "A documented process is maintained for detecting, escalating and reporting a personal data breach within the timescale in clause 6."
    }
  ],
  "annex3": [
    [
      "Sub-processor",
      "Purpose",
      "Location"
    ],
    [
      "Vercel",
      "Cloud hosting of the analysis application",
      "United Kingdom, London (lhr1)"
    ],
    [
      "Supabase",
      "Database, authentication and backend functions",
      "United Kingdom (London, eu-west-2)"
    ],
    [
      "Resend",
      "Transactional email for user invites",
      "Ireland (EU, eu-west-1)"
    ]
  ],
  "signatureIntro": "Signatures are needed only where the Controller signs this DPA instead of accepting it through the Dioro Terms of Use.",
  "signatures": [
    [
      "For SaaSed.co Limited:",
      "",
      "",
      "_________________________________",
      "Name: Anders Gustafsson",
      "Title: Co-founder",
      "Date:"
    ],
    [
      "For the Controller:",
      "",
      "",
      "_________________________________",
      "Name:",
      "Title:",
      "Date:"
    ]
  ]
};

const pageStyles = `
.dpa-page {
  --dpa-background: #fae396;
  --dpa-ink: #1a1408;
  --dpa-accent: #f5cc00;
  --dpa-hairline: rgba(26,20,8,0.2);
  min-height: 100vh;
  width: 100%;
  background: var(--dpa-background);
  color: var(--dpa-ink);
  font-family: "DM Sans", Georgia, serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.7;
  letter-spacing: 0;
}
.dpa-page * { box-sizing: border-box; }
.dpa-page .dpa-document { max-width: 820px; margin: 0 auto; padding: 56px 24px 96px; }
.dpa-page h1, .dpa-page h2 { color: var(--dpa-ink); font-family: "Bricolage Grotesque", Georgia, serif; font-weight: 700; letter-spacing: 0; }
.dpa-page h1 { font-size: 40px; line-height: 1.1; margin: 0 0 20px; }
.dpa-page h2 { font-size: 28px; line-height: 1.2; margin: 56px 0 16px; }
.dpa-page .dpa-accent { width: 56px; height: 3px; background: var(--dpa-accent); margin-bottom: 20px; }
.dpa-page .dpa-subtitle { font-size: 15px; font-style: italic; margin: 0 0 28px; }
.dpa-page .dpa-intro { display: grid; gap: 14px; }
.dpa-page p { margin: 0; font-size: inherit; line-height: inherit; }
.dpa-page .dpa-clauses { display: grid; gap: 14px; }
.dpa-page .dpa-clause { display: grid; grid-template-columns: 48px minmax(0, 1fr); gap: 0; break-inside: avoid; }
.dpa-page .dpa-number, .dpa-page tbody th { font-weight: 500; }
.dpa-page .dpa-security { list-style: none; padding: 0; margin: 0; display: grid; gap: 14px; }
.dpa-page strong { font-weight: 500; }
.dpa-page .dpa-table-scroll { max-width: 100%; overflow-x: auto; }
.dpa-page table { width: 100%; min-width: 540px; border-collapse: collapse; border: 0; font: inherit; color: var(--dpa-ink); background: transparent; }
.dpa-page th, .dpa-page td { text-align: left; vertical-align: top; padding: 12px 14px 12px 0; }
.dpa-page thead th { font-size: 11px; font-weight: 500; text-transform: uppercase; letter-spacing: 0.1em; border-bottom: 1px solid var(--dpa-ink); }
.dpa-page tbody tr:not(:last-child) { border-bottom: 1px solid var(--dpa-hairline); }
.dpa-page .dpa-signature-intro { margin: 40px 0 24px; }
.dpa-page .dpa-signatures { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 32px; }
.dpa-page .dpa-signature { border-top: 1px solid var(--dpa-ink); padding-top: 12px; min-width: 0; break-inside: avoid; }
.dpa-page .dpa-signature-label { font-size: 11px; font-weight: 500; text-transform: uppercase; letter-spacing: 0.1em; }
.dpa-page .dpa-signature-line { overflow-wrap: anywhere; }
html:has(.dpa-page) { scroll-behavior: smooth; }
@media (max-width: 600px) {
  .dpa-page h1 { font-size: 32px; }
  .dpa-page h2 { font-size: 24px; }
  .dpa-page .dpa-clause { grid-template-columns: 40px minmax(0, 1fr); }
  .dpa-page .dpa-signatures { grid-template-columns: minmax(0, 1fr); }
}
@media (prefers-reduced-motion: reduce) { html:has(.dpa-page) { scroll-behavior: auto; } }
@media print {
  body:has(.dpa-page), .dpa-page { background: transparent; }
  body:has(.dpa-page) header, body:has(.dpa-page) footer { display: none; }
  .dpa-page .dpa-document { max-width: none; padding: 0; }
  .dpa-page h1, .dpa-page h2 { break-after: avoid; }
  .dpa-page .dpa-clause, .dpa-page tr { break-inside: avoid; }
  .dpa-page .dpa-table-scroll { overflow: visible; }
  .dpa-page table { min-width: 0; }
}
`;

const pageTitle = "Data Processing Agreement — SaaSed.co";
const pageDescription = "Data Processing Agreement between SaaSed.co Limited as Processor and the Customer as Controller, effective 03.11.2026.";

export const Route = createFileRoute("/dioro_dpa")({
  head: () => ({
    meta: [
      { title: pageTitle },
      { name: "description", content: pageDescription },
      { property: "og:title", content: pageTitle },
      { property: "og:description", content: pageDescription },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: pageTitle },
      { name: "twitter:description", content: pageDescription },
      { name: "robots", content: "noindex, nofollow, noarchive, nosnippet" },
      { name: "googlebot", content: "noindex, nofollow" },
    ],
    links: [{ rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@700&family=DM+Sans:wght@400;500&display=swap" }],
    styles: [{ children: pageStyles }],
  }),
  component: DioroDpaPage,
});

function DocumentTable({ rows }: { rows: string[][] }) {
  const [headers, ...body] = rows;
  return (
    <div className="dpa-table-scroll" role="region" aria-label={headers?.join(" / ")} tabIndex={0}>
      <table>
        <thead><tr>{headers?.map((label) => <th key={label} scope="col">{label}</th>)}</tr></thead>
        <tbody>{body.map((row) => <tr key={row[0]}>{row.map((cell, index) => index === 0 ? <th key={index} scope="row">{cell}</th> : <td key={index}>{cell}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

function DioroDpaPage() {
  return (
    <div className="dpa-page">
      <article className="dpa-document">
        <h1>{documentText.title}</h1>
        <div className="dpa-accent" aria-hidden="true" />
        <p className="dpa-subtitle">{documentText.subtitle}</p>
        <div className="dpa-intro">{documentText.intro.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}</div>
        {documentText.sections.map((section) => (
          <section key={section.title}>
            <h2>{section.title}</h2>
            <div className="dpa-clauses">{section.clauses.map((clause) => <div className="dpa-clause" key={clause.number}><span className="dpa-number">{clause.number}</span><p>{clause.text}</p></div>)}</div>
          </section>
        ))}
        <section><h2>Annex 1 — Processing Details</h2><DocumentTable rows={documentText.annex1} /></section>
        <section>
          <h2>Annex 2 — Security Measures</h2>
          <ol className="dpa-security">{documentText.security.map((item) => <li className="dpa-clause" key={item.number}><span className="dpa-number">{item.number}</span><p><strong>{item.lead}</strong>{" "}{item.text}</p></li>)}</ol>
        </section>
        <section><h2>Annex 3 — Approved sub-processors</h2><DocumentTable rows={documentText.annex3} /></section>
        <p className="dpa-signature-intro">{documentText.signatureIntro}</p>
        <div className="dpa-signatures">{documentText.signatures.map(([label, ...lines]) => <div className="dpa-signature" key={label}><p className="dpa-signature-label">{label}</p>{lines.map((line, index) => <p className="dpa-signature-line" key={index}>{line}</p>)}</div>)}</div>
      </article>
    </div>
  );
}
